import React, { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { MINISTRY_DEFINITIONS } from "../constants/permissions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIVE_MINUTES = 5 * 60 * 1000;
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const STORAGE_KEYS = {
  completed: "ys_lead_capture_completed",
  lastClosedAt: "ys_lead_capture_last_closed_at",
  closeCount: "ys_lead_capture_close_count",
  hideUntil: "ys_lead_capture_hide_until",
};

const interestGroups = [
  {
    label: "Nation & Covenant",
    options: [
      { value: "become_national", label: "Become YaSHaRUN" },
      { value: "covenant_governance", label: "Constitution / Covenant / Governance" },
      { value: "right_ruling_mediation", label: "Right-Ruling / Mediation / Records" },
    ],
  },
  {
    label: "Support & Purchases",
    options: [
      { value: "support_donate", label: "Donate / Support the Work" },
      { value: "subscriber_access", label: "Subscriber Access / Monthly Support" },
      { value: "merch_shop", label: "Merch / Shop / Purchases" },
    ],
  },
  {
    label: "YaSHaRUN Ministries & Programs (YaSHaRUN membership required for access)",
    options: MINISTRY_DEFINITIONS.map(({key, label}) => ({value:key,label})),
  },
  {
    label: "Music, Broadcasting & Learning",
    options: [
      { value: "music", label: "Music & Releases" },
      { value: "records", label: "Record Label / Artists" },
      { value: "publishing", label: "Publishing / Writers" },
      { value: "lives", label: "Broadcasts & Live Sessions" },
      { value: "teachings", label: "Teachings" },
      { value: "studies", label: "Studies" },
      { value: "tutorials", label: "Tutorials" },
      { value: "sneak_peeks", label: "Sneak Peeks" },
      { value: "exclusives", label: "Exclusive Announcements" },
      { value: "weekly_summary", label: "Weekly Summaries" },
      { value: "monthly_digest", label: "Monthly Digests" },
      { value: "feasts", label: "Feasts & Appointed Times" },
      { value: "events", label: "Gatherings / Events" },
      { value: "programs", label: "Programs" },
    ],
  },
  {
    label: "Projects & Campaigns",
    options: [
      { value: "fightfitash", label: "Fight Fi Tash Campaign" },
      { value: "infrastructure_solar", label: "Infrastructure / Solar / Works" },
      { value: "music_media_content", label: "Music / Media / Content" },
      { value: "other", label: "Other" },
    ],
  },
];

function getStoredNumber(key) {
  const value = Number(localStorage.getItem(key));
  return Number.isFinite(value) ? value : 0;
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function hasKnownAccountOrPurchasePath() {
  const nationalsToken = localStorage.getItem("nationals_token");
  const nationalsUser = safeJsonParse(localStorage.getItem("nationals_user"));
  const subscribersToken = localStorage.getItem("subscribers_token");
  const subscribersUser = safeJsonParse(localStorage.getItem("subscribers_user"));
  const portalUser = safeJsonParse(localStorage.getItem("portal_user"));

  if (nationalsToken || subscribersToken) return true;

  const possibleUsers = [nationalsUser, subscribersUser, portalUser].filter(Boolean);

  const hasAccountRole = possibleUsers.some((user) => {
    const roles = user.roles || [];
    const purchasedItems = user.purchased_items || [];
    return (
      roles.includes("national") ||
      roles.includes("subscriber") ||
      user.subscription_status === "active" ||
      user.provider_customer_id ||
      purchasedItems.length > 0
    );
  });

  if (hasAccountRole) return true;

  const path = window.location.pathname.toLowerCase();

  const accountOrPurchasePaths = [
    "/portal",
    "/nationals",
    "/subscribers",
    "/checkout-success",
    "/success",
    "/thank-you",
    "/donate/success",
  ];

  return accountOrPurchasePaths.some((accountPath) => path.includes(accountPath));
}

export function nextLeadCaptureEligibleAt(now = Date.now()) {
  if (hasKnownAccountOrPurchasePath()) return null;
  if (localStorage.getItem(STORAGE_KEYS.completed) === "true") return null;
  const hideUntil = getStoredNumber(STORAGE_KEYS.hideUntil);
  const lastClosedAt = getStoredNumber(STORAGE_KEYS.lastClosedAt);
  return Math.max(now, hideUntil, lastClosedAt ? lastClosedAt + FIVE_MINUTES : 0);
}

function markLeadCompleted() {
  localStorage.setItem(STORAGE_KEYS.completed, "true");
  localStorage.removeItem(STORAGE_KEYS.lastClosedAt);
  localStorage.removeItem(STORAGE_KEYS.closeCount);
  localStorage.removeItem(STORAGE_KEYS.hideUntil);
}

function markAccountSuppressed() {
  localStorage.removeItem(STORAGE_KEYS.lastClosedAt);
  localStorage.removeItem(STORAGE_KEYS.closeCount);
  localStorage.removeItem(STORAGE_KEYS.hideUntil);
}

function markLeadModalClosed() {
  const now = Date.now();
  const closeCount = getStoredNumber(STORAGE_KEYS.closeCount) + 1;

  localStorage.setItem(STORAGE_KEYS.lastClosedAt, String(now));
  localStorage.setItem(STORAGE_KEYS.closeCount, String(closeCount));

  if (closeCount >= 3) {
    localStorage.setItem(STORAGE_KEYS.hideUntil, String(now + THIRTY_DAYS));
    localStorage.setItem(STORAGE_KEYS.closeCount, "0");
  }
}

export default function LeadCaptureModal({ isOpen, onClose, onComplete }) {
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    state_province_region: "",
    locality: "",
    postal_code: "",
    interests: [],
    social_profile_links: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!formData.interests.length) {
      newErrors.interest = "Please select your interest";
    }

    if (
      formData.interests.includes("become_national") &&
      !formData.social_profile_links.trim()
    ) {
      newErrors.social_profile_links = "Social media links required for YaSHaRUN";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClose = () => {
    markLeadModalClosed();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      markLeadCompleted();
      toast.success("Submitted successfully");
      onComplete?.();
      onClose?.();
    } catch (error) {
      console.error("Failed to submit lead:", error);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="bg-royal-purple text-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto modal-safe"
        data-testid="lead-capture-modal"
      >
        <div className="sticky top-0 bg-royal-purple p-4 border-b border-purple-700 flex items-center justify-between">
          <div>
            <div className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider mb-1">
              Stay Connected
            </div>

            <h2 className="font-heading text-xl text-white">
              Get Kingdom Updates
            </h2>
          </div>

          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-purple-700 transition-colors"
            data-testid="lead-modal-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-purple-200 text-sm">
            Receive updates on events, teachings, nation-building, ministry work,
            support opportunities, and projects of Yunah&apos;Shach&apos;Qal
            Yashar&apos;al.
          </p>

          <div>
            <Label className="form-label text-kingdom-gold">Full Name *</Label>
            <Input
              type="text"
              value={formData.full_name}
              onChange={(e) => handleChange("full_name", e.target.value)}
              className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
              placeholder="Your full name"
              data-testid="lead-name-input"
              style={{ fontSize: "16px" }}
            />
            {errors.full_name && (
              <p className="text-red-400 text-xs mt-1">{errors.full_name}</p>
            )}
          </div>

          <div>
            <Label className="form-label text-kingdom-gold">Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
              placeholder="your@email.com"
              data-testid="lead-email-input"
              style={{ fontSize: "16px" }}
            />
            {errors.email && (
              <p className="text-red-400 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <Label className="form-label text-kingdom-gold">Phone *</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
              placeholder="+1 (555) 123-4567"
              data-testid="lead-phone-input"
              style={{ fontSize: "16px" }}
            />
            {errors.phone && (
              <p className="text-red-400 text-xs mt-1">{errors.phone}</p>
            )}
          </div>

          <div>
            <Label className="form-label text-kingdom-gold">Country *</Label>
            <Input
              type="text"
              value={formData.country}
              onChange={(e) => handleChange("country", e.target.value)}
              className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
              placeholder="Your country"
              data-testid="lead-country-input"
              style={{ fontSize: "16px" }}
            />
            {errors.country && (
              <p className="text-red-400 text-xs mt-1">{errors.country}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="form-label text-kingdom-gold">Town / Village / City / District</Label>
              <Input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
                placeholder="Town, village, city or district (if applicable)"
                style={{ fontSize: "16px" }}
              />
            </div>

            <div>
              <Label className="form-label text-kingdom-gold">
                Parish / State / Province / County / Region
              </Label>
              <Input
                type="text"
                value={formData.state_province_region}
                onChange={(e) =>
                  handleChange("state_province_region", e.target.value)
                }
                className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
                placeholder="Parish, state, county or region (if applicable)"
                style={{ fontSize: "16px" }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="form-label text-kingdom-gold">Other Local Area / District</Label>
              <Input value={formData.locality} onChange={e=>handleChange("locality",e.target.value)}
                className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
                placeholder="Local administrative area (optional)" />
            </div>
            <div>
              <Label className="form-label text-kingdom-gold">Postal / ZIP Code</Label>
              <Input value={formData.postal_code} onChange={e=>handleChange("postal_code",e.target.value)}
                className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500"
                placeholder="Postal code, if applicable" />
            </div>
          </div>
          <fieldset className="space-y-3">
            <legend className="form-label text-kingdom-gold font-semibold">Areas of Interest *</legend>
            <p className="text-xs text-purple-200">Choose all that apply. Every selection enrolls you in its related updates, along with the general newsletter.</p>
            {interestGroups.map(group => <div key={group.label}>
              <p className="text-sm font-semibold text-kingdom-gold mb-1">{group.label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {group.options.map(option => <label key={option.value} className="flex gap-2 items-start text-sm text-purple-100">
                  <input type="checkbox" className="mt-1" checked={formData.interests.includes(option.value)}
                    onChange={e => handleChange("interests", e.target.checked
                      ? [...formData.interests, option.value]
                      : formData.interests.filter(value => value !== option.value))} />
                  {option.label}
                </label>)}
              </div>
            </div>)}
            {errors.interest && <p className="text-red-400 text-xs">{errors.interest}</p>}
          </fieldset>

          <div>
            <Label className="form-label text-kingdom-gold">
              Social Media Links{" "}
              {formData.interest === "become_national" && "*"}
            </Label>
            <Textarea
              value={formData.social_profile_links}
              onChange={(e) =>
                handleChange("social_profile_links", e.target.value)
              }
              className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500 min-h-[60px]"
              placeholder="Your social media profile links"
              style={{ fontSize: "16px" }}
            />
            {errors.social_profile_links && (
              <p className="text-red-400 text-xs mt-1">
                {errors.social_profile_links}
              </p>
            )}
          </div>

          <div>
            <Label className="form-label text-kingdom-gold">
              Message Optional
            </Label>
            <Textarea
              value={formData.message}
              onChange={(e) => handleChange("message", e.target.value)}
              className="form-input bg-white border-purple-300 !text-slate-900 placeholder:!text-slate-500 min-h-[80px]"
              placeholder="Tell us more about yourself, your skills, your service area, or your questions"
              style={{ fontSize: "16px" }}
            />
          </div>

          <p className="text-xs text-purple-100">Submitting subscribes or re-subscribes you to our general newsletter and updates matching the areas you selected. Change your email preferences or unsubscribe using the link in any email. Restricted content still requires the appropriate account access.</p>
          <Button
            type="submit"
            disabled={loading}
            className="w-full btn-gold py-3"
            data-testid="lead-submit-btn"
          >
            {loading ? "Submitting..." : "Stay Connected"}
          </Button>

          <p className="text-xs text-purple-300 text-center">
            Already YaSHaRUN, Subscriber, supporter, or purchaser? This form
            should stop appearing once your account, purchase, or submission is
            recognized.
          </p>
        </form>
      </div>
    </div>
  );
}
